import PropTypes from 'prop-types';
import React from 'react';
import { Query } from 'react-apollo';
import { BasePage, Helmet } from '@sm/webassets';
import { Grid, Row, Col } from '@sm/wds-react';

import MainHeading from '~pricing/components/MainHeading';
import PricingNavigation from '~pricing/components/PricingNavigation';
import { Package, SummaryFooter, SummaryComparisonTable } from '~pricing/components/Summary';
import { INDIVIDUAL_PACKAGES, TEAMS_PACKAGES } from '~pricing/lib/constants';
import SUMMARY_PAGE_RENDERERS from '~pricing/lib/summary-packages';
import SummaryComparisonTableDef from '~pricing/lib/definitions/SummaryComparisonTable';

import { getDefaultExperience } from '~pricing/package-prices.graphql';

const createPackagesList = (packages, packageSet) => {
  // The provided package list is both the filter and determines the order
  return packageSet.map(packageName => packages.find(packageObj => packageObj.package === packageName));
};

const renderPricingSummary = (allPackages, packageSet) => {
  const displayedPackages = createPackagesList(allPackages, packageSet);

  return (
    <>
      <div className="packages wds-flex wds-flex--x-center wds-flex--stretch" key="packageList">
        {displayedPackages.map(pricingPackage => (
          <Package {...pricingPackage} key={`package-${pricingPackage.package}`} />
        ))}
      </div>
      <SummaryComparisonTable
        packages={displayedPackages}
        packagesConfig={SUMMARY_PAGE_RENDERERS}
        comparisonTableDef={SummaryComparisonTableDef}
      />
      <SummaryFooter packages={allPackages} />
    </>
  );
};

const PricingSummary = ({
  match: {
    params: { packageTypes = 'teams' },
  },
}) => {
  const packageSet = packageTypes === 'individual' ? INDIVIDUAL_PACKAGES : TEAMS_PACKAGES;

  return (
    <BasePage pageId="Pricing Summary" legacyWeb="pricingweb" sticky={false}>
      <Helmet>
        <base target="_top" href="/" />
        <title>SurveyMonkey Plans and Pricing</title>
        <meta
          name="keywords"
          content="questionnaire, questionnaires, questionaire, questionaires, free online survey, free online surveys"
        />
        <meta
          name="description"
          content="Create and publish online surveys in minutes, and view results graphically and in real time. SurveyMonkey provides free online questionnaire and survey software."
        />
      </Helmet>
      <Grid>
        <Row>
          <Col>
            <main>
              <header>
                <MainHeading />
                <PricingNavigation packageType={packageTypes} />
              </header>
              <Query
                query={getDefaultExperience}
                variables={{
                  countryCode: 'US',
                  languageCode: 'en',
                  experienceName: 'pricingweb',
                }}
              >
                {({ loading, error, data }) => (
                  <Choose>
                    <When condition={error}>
                      {/* TODO: Real error state */}
                      <h1>Error</h1>
                    </When>
                    <When condition={loading}>
                      {/* TODO: This will never been seen in a server side rendered context. Probably change up the conditions? */}
                      <h1 data-testid="Summary__PricingLoading">Loading</h1>
                    </When>
                    <Otherwise>{renderPricingSummary(data.pricingExperience.package, packageSet)}</Otherwise>
                  </Choose>
                )}
              </Query>
            </main>
          </Col>
        </Row>
      </Grid>
    </BasePage>
  );
};

PricingSummary.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      packageTypes: PropTypes.string,
    }),
  }),
};

PricingSummary.defaultProps = {
  match: {
    params: {
      packageTypes: 'teams',
    },
  },
};

export default PricingSummary;
